import React, { useEffect, useState } from 'react';
import lodash from 'lodash';
import { Collapse } from '@mui/material';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import { procurementApi } from '@/main/api';
import { ReasonFilterRequest } from '@/main/models/request';
import {
  getDefaultCountryOptionDrop,
  getTranslateByKey
} from '@/main/services/utils';
import * as S from './styles';
import {
  GetSupplierBulkChangeItemsResponse,
  ManageStatusItemsResponse,
  ProcurementHeaderViewOrderResponse
} from '@/main/models/response';
import Image from '@mui/icons-material/Collections';
import KOGImage from '@/main/components/shared/kogImage/kogImage';
import ColumnLabel from '@/main/components/common/columnLabel/columnLabel';
import KogCheckbox from '@/main/components/shared/checkbox/kogCheckbox';
import KOGFileUploader from '@/main/components/shared/fileUploader/fileUploader';
import KogModalComponent from '@/main/components/modal/ModalComponent';
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';
import { convertImageToBase64 } from '@/main/services/utilsFile';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import { useForm } from 'react-hook-form';
import { convertStringForBoolean } from '@/main/services/utilsString';
import { getReason, useReasonReducer } from '@/main/store/reason';
import { ReasonTypeEnum } from '@/main/services/enum/ReasonEnum';
import { useDispatch } from 'react-redux';
import { useCountryReducer } from '@/main/store/country';
import { toast } from 'react-toastify';
import { KeyValue, KeyValueDefault } from '@/main/models/common';
import QuoteProcurementPartInfo from '@/main/components/shared/quoteProcurementPartInfo/quoteProcurementPartInfo';
import { useVisualIdentityReducer } from '@/main/store/visualIdentity';

interface ImageInfo {
  id: string;
  files: File[];
}
interface IModalCancelReturnProps {
  isModalCancel: boolean;
  isModalReturn: boolean;
  dataOrder: GetSupplierBulkChangeItemsResponse[];
  guidProcurement: string;
  handleModalCancelReturn: () => void;
  handleUpdateQuoteCountStatus: () => void;
  handleUpdateQuoteGridStatus: (
    guid: string,
    newStatus: number,
    newStatusSupplierName: string
  ) => void;
  dataHeader: ProcurementHeaderViewOrderResponse | null;
  loadViewOrder: () => Promise<void>;
  setResultDataManageStatus: (data: ManageStatusItemsResponse | null) => void;
}

const INITIAL_FORM_STATE: {
  [key: string]: null | KeyValue | KeyValueDefault | string | string[];
} = {
  country: null,
  reason: null,
  description: '',
  optionsSelected: []
};
type ICancelAndReturnOrderForm = typeof INITIAL_FORM_STATE;

function ModalCancelReturn({
  isModalCancel,
  isModalReturn,
  dataOrder,
  dataHeader,
  guidProcurement,
  handleModalCancelReturn,
  handleUpdateQuoteCountStatus,
  handleUpdateQuoteGridStatus,
  loadViewOrder,
  setResultDataManageStatus
}: IModalCancelReturnProps) {
  const dispatch = useDispatch();
  const { countriesUser: countryReducer } = useCountryReducer();
  const { reason: reasonReducer } = useReasonReducer();
  const {
    visualIdentity: { colors }
  } = useVisualIdentityReducer();
  const { control, setValue, handleSubmit } =
    useForm<ICancelAndReturnOrderForm>({
      mode: 'onChange',
      defaultValues: INITIAL_FORM_STATE
    });

  const [orderData, setOrderData] = useState<
    GetSupplierBulkChangeItemsResponse[]
  >([]);
  const [orderDataLoading, setOrderDataLoading] = useState<boolean>(false);
  const [operationLoading, setOperationLoading] = useState<boolean>(false);
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);
  const [optionsSelected, setOptionsSelected] = useState<string[]>([]);
  const [openCollapseIndex, setOpenCollapseIndex] = useState<string | null>(
    null
  );
  const [imagesGeneral, setImagesGeneral] = useState<ImageInfo[]>([]);
  const [images, setImages] = useState<File[]>([]);
  const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<{
    [key: string]: ICancelAndReturnOrderForm;
  }>({});

  useEffect(() => {
    if (guidProcurement && orderData.length > 0) {
      const initialFormValues: {
        [key: string]: ICancelAndReturnOrderForm;
      } = {};
      orderData.forEach(supplier => {
        initialFormValues[supplier.guidSupplier] = { ...INITIAL_FORM_STATE };
      });
      setFormValues(initialFormValues);
    }
  }, [guidProcurement, orderData]);

  useEffect(() => {
    if (guidProcurement) {
      getOrderData();
    }
  }, [guidProcurement]);

  useEffect(() => {
    if (orderData.length > 0) {
      const initialFormValues: {
        [key: string]: ICancelAndReturnOrderForm;
      } = {};

      orderData.forEach(supplier => {
        const supplierFormValues: ICancelAndReturnOrderForm = {
          country: null,
          reason: null,
          description: '',
          optionsSelected: supplier.updateableParts.map(
            (item: any) => item.guidProcurementSupplierItem
          )
        };

        initialFormValues[supplier.guidSupplier] = supplierFormValues;
      });

      setFormValues(initialFormValues);

      let allGuids: string[] = optionsSelected;
      orderData.forEach(orderItem => {
        allGuids = allGuids.concat(
          orderItem.updateableParts.map(
            (item: any) => item.guidProcurementSupplierItem
          )
        );
      });
      setOptionsSelected(prev => [...new Set(allGuids)]);

      const initialSelectedSuppliers = orderData.map(
        orderItem => orderItem.guidSupplier
      );
      setSelectedSuppliers(initialSelectedSuppliers);
    }
  }, [orderData]);

  function getOrderData() {
    setOrderDataLoading(true);
    let status = isModalCancel ? 'Canceled' : 'Returned';
    procurementApi
      .getBulkChangeItems(String(guidProcurement), status)
      .then(response => {
        if (response && response.data && response.data.result) {
          const resData = response.data.result;
          setOrderData(resData.supplierPartsUpdateables);
        }
      })
      .finally(() => {
        setOrderDataLoading(false);
      });

    const countryUser = getDefaultCountryOptionDrop(countryReducer.data);
    if (countryUser === null) return;

    const requestReason: ReasonFilterRequest = {
      guidCountry: String(countryUser.key),
      idReasonType: ReasonTypeEnum.Cancel
    };
    dispatch(getReason(requestReason));
  }

  function handleToggleSelectAll(supplier: string) {
    setSelectedSuppliers(prevSelected => {
      if (prevSelected.includes(supplier)) {
        setFormValues(prevValues => {
          const updatedValues = { ...prevValues };

          const guidForRemove: string[] = [];
          orderData.forEach(orderItem => {
            if (orderItem.guidSupplier === supplier) {
              guidForRemove.push(
                ...orderItem.updateableParts.map(
                  (item: any) => item.guidProcurementSupplierItem
                )
              );
            }
          });

          updatedValues[supplier] = {
            ...updatedValues[supplier],
            optionsSelected: []
          };

          setOptionsSelected(prevOptions =>
            prevOptions.filter(guid => !guidForRemove.includes(guid))
          );

          return updatedValues;
        });

        return prevSelected.filter(
          selectedSupplier => selectedSupplier !== supplier
        );
      } else {
        setFormValues(prevValues => {
          const updatedValues = { ...prevValues };

          const allGuids: string[] = [];
          orderData.forEach(orderItem => {
            if (orderItem.guidSupplier === supplier) {
              allGuids.push(
                ...orderItem.updateableParts.map(
                  (item: any) => item.guidProcurementSupplierItem
                )
              );
            }
          });

          updatedValues[supplier] = {
            ...updatedValues[supplier],
            optionsSelected: allGuids
          };

          setOptionsSelected(prevOptions => [
            ...new Set(prevOptions.concat(allGuids))
          ]);

          return updatedValues;
        });

        return [...prevSelected, supplier];
      }
    });
  }

  function handleToggleCheckbox(guid: string, supplier: string) {
    const cloneOptionsSelected = lodash.cloneDeep(optionsSelected);
    const index = cloneOptionsSelected.indexOf(guid);

    if (index > -1) {
      cloneOptionsSelected.splice(index, 1);
    } else {
      cloneOptionsSelected.push(guid);
    }

    setOptionsSelected(cloneOptionsSelected);

    setFormValues(prevValues => {
      const existingGuidIndex = (
        prevValues[supplier]?.optionsSelected || []
      ).indexOf(guid);

      if (existingGuidIndex > -1) {
        const updatedOptionsSelected = [
          ...prevValues[supplier]?.optionsSelected
        ];
        updatedOptionsSelected.splice(existingGuidIndex, 1);

        return {
          ...prevValues,
          [supplier]: {
            ...prevValues[supplier],
            optionsSelected: updatedOptionsSelected
          }
        };
      } else {
        return {
          ...prevValues,
          [supplier]: {
            ...prevValues[supplier],
            optionsSelected: [
              ...(prevValues[supplier]?.optionsSelected || []),
              guid
            ]
          }
        };
      }
    });

    const allCheckboxesSelected = orderData
      .filter(orderItem => orderItem.guidSupplier === supplier)
      .every(orderItem =>
        orderItem.updateableParts.every((item: any) =>
          cloneOptionsSelected.includes(item.guidProcurementSupplierItem)
        )
      );

    if (allCheckboxesSelected) {
      setSelectedSuppliers(prevSelected => [
        ...new Set([...prevSelected, supplier])
      ]);
    } else {
      setSelectedSuppliers(prevSelected =>
        prevSelected.filter(selectedSupplier => selectedSupplier !== supplier)
      );
    }
  }

  const handleClick = (supplierItem: string) => {
    const filteredImages = imagesGeneral
      .filter(item => item.id === supplierItem)
      .reduce((acc, item) => [...acc, ...item.files], [] as File[]);

    setImages(filteredImages);
    setOpenCollapseIndex(prevIndex =>
      prevIndex === supplierItem ? null : supplierItem
    );
  };

  async function createProcurementProcessStatusRequest(
    dataOrder: GetSupplierBulkChangeItemsResponse[],
    formValues: ICancelAndReturnOrderForm,
    imagesGeneral: ImageInfo[]
  ) {
    const result = [];

    const selectedSuppliersAux: string[] = [];

    dataOrder.forEach(orderItem => {
      orderItem.procurementSupplierItems.forEach(item => {
        if (
          optionsSelected.includes(item.guidProcurementSupplierItem) &&
          !selectedSuppliersAux.includes(orderItem.guidSupplier)
        ) {
          selectedSuppliersAux.push(orderItem.guidSupplier);
        }
      });
    });

    for (const supplier of dataOrder) {
      const supplierFormValues = formValues[supplier.guidSupplier];

      if (
        supplierFormValues &&
        selectedSuppliersAux.includes(supplier.guidSupplier)
      ) {
        for (const item of supplier.procurementSupplierItems) {
          if (optionsSelected.includes(item.guidProcurementSupplierItem)) {
            const imageInfo = imagesGeneral.find(
              imageInfo => imageInfo.id === item.guidProcurementSupplierItem
            );
            const imagesRequest = [];

            if (imageInfo) {
              for (const file of imageInfo.files) {
                const base64Image = await convertImageToBase64(file);
                imagesRequest.push({
                  imageName: base64Image.name,
                  imageUpload: `data:image/jpeg;base64,${base64Image.base64Image}`
                });
              }
            }

            const requestItem = {
              confirm: false,
              deliveryBySupplier: supplier.deliveryBySupplier,
              guidProcurement: guidProcurement,
              guidProcurementSupplier: supplier.guidProcurementSupplier,
              imagesRequest: imagesRequest,
              procurementSupplierItems: [
                {
                  guidProcurementSupplierItem: item.guidProcurementSupplierItem
                }
              ],
              guidReason: supplierFormValues.reason.key,
              idReason: Number(supplierFormValues.reason.default),
              reasonDescription: convertStringForBoolean(
                supplierFormValues.reason.default
              )
                ? supplierFormValues.description
                : ''
            };

            result.push(requestItem);
          }
        }
      }
    }

    return result;
  }

  const handleConfirmAction = async () => {
    setOperationLoading(true);
    const procurementStatusRequests =
      await createProcurementProcessStatusRequest(
        dataOrder,
        formValues,
        imagesGeneral
      );

    if (isModalCancel) {
      procurementApi
        .cancelBulkItems(procurementStatusRequests)
        .then(responseApi => {
          if (responseApi && responseApi.data && responseApi.data.result) {
            const resData = responseApi.data.result;
            toast.success(
              getTranslateByKey('information.partsCanceledSuccessfully')
            );
            if (resData.orderNewStatusIdChanged) {
              handleUpdateQuoteCountStatus();
              handleUpdateQuoteGridStatus(
                guidProcurement,
                resData.orderNewStatusIdChanged,
                resData.orderNewStatusNameChanged
              );
            }
            handleModalCancelReturn();
            setTimeout(() => {
              window.location.reload();
            }, 2500);
            loadViewOrder();
            setResultDataManageStatus(resData);
          }
        })
        .finally(() => {
          setOperationLoading(false);
        });
    } else {
      procurementApi
        .returnBulkItems(procurementStatusRequests)
        .then(responseApi => {
          if (responseApi && responseApi.data && responseApi.data.result) {
            const resData = responseApi.data.result;
            toast.success(
              getTranslateByKey('information.partsReturnedSuccessfully')
            );
            if (resData.orderNewStatusIdChanged) {
              handleUpdateQuoteCountStatus();
              handleUpdateQuoteGridStatus(
                guidProcurement,
                resData.orderNewStatusIdChanged,
                resData.orderNewStatusNameChanged
              );
            }
            handleModalCancelReturn();
            setTimeout(() => {
              window.location.reload();
            }, 2500);
            loadViewOrder();
            setResultDataManageStatus(resData);
          }
        })
        .finally(() => {
          setOperationLoading(false);
        });
    }
  };

  function onSubmitCancelAndReturn() {
    setOpenModalConfirm(true);
  }

  function renderText(
    text: String | Number | undefined,
    hasTooltip: boolean = false
  ) {
    if (!text) return '-';

    if (hasTooltip) {
      return <S.TooltipText title={text as string}>{text}</S.TooltipText>;
    }
    return text;
  }
  const isDisabledSelect =
    (isModalCancel || isModalReturn) && dataHeader?.isStripePayment;

  return (
    <S.Container onSubmit={handleSubmit(onSubmitCancelAndReturn)} noValidate>
      {!orderDataLoading ? (
        <>
          {orderData?.map((supplier, index) => (
            <div
              style={{
                backgroundColor: colors.grayLight,
                marginBottom: '1rem',
                padding: '.5rem'
              }}>
              <S.ContainerSupplier>
                <S.SupplierName title={supplier.supplierName}>
                  {renderText(supplier.supplierName, true)}
                </S.SupplierName>
                <KogCheckbox
                  key={`checkbox-${supplier.guidSupplier}`}
                  checked={selectedSuppliers.includes(supplier.guidSupplier)}
                  value={selectedSuppliers.includes(supplier.guidSupplier)}
                  disabled={isDisabledSelect}
                  onChange={() => handleToggleSelectAll(supplier.guidSupplier)}
                  style={{ marginRight: 3 }}
                  id={`id-registerSupplier-checkbox-selectAll-${supplier.guidSupplier}`}
                />
              </S.ContainerSupplier>
              {supplier.updateableParts?.map(part => (
                <S.ContainerPart>
                  <S.Column
                    customWidth={
                      optionsSelected.includes(
                        part.guidProcurementSupplierItem
                      ) && isModalReturn
                        ? '12%'
                        : '16%'
                    }>
                    <S.BoxImagePart>
                      <KOGImage src={part.partImage} />
                    </S.BoxImagePart>
                    <QuoteProcurementPartInfo
                      data={part.quoteProcurementPartInfo}
                      renderType="column"
                    />
                    <ColumnLabel
                      label={''}
                      content={<S.Quantity> {part.partQuantity}</S.Quantity>}
                    />
                    {optionsSelected.includes(
                      part.guidProcurementSupplierItem
                    ) &&
                      isModalReturn && (
                        <S.ButtonImage
                          type="button"
                          onClick={() =>
                            handleClick(part.guidProcurementSupplierItem)
                          }>
                          <Image
                            id="id-searchUserGroup-icon-image"
                            style={{ color: colors.pantone2726C }}
                          />
                        </S.ButtonImage>
                      )}
                    <KogCheckbox
                      style={{
                        justifyContent: 'right',
                        paddingRight:
                          optionsSelected.includes(
                            part.guidProcurementSupplierItem
                          ) && isModalReturn
                            ? '12px'
                            : '8px'
                      }}
                      onChange={() =>
                        handleToggleCheckbox(
                          part.guidProcurementSupplierItem,
                          supplier.guidSupplier
                        )
                      }
                      disabled={isDisabledSelect}
                      checked={optionsSelected.includes(
                        part.guidProcurementSupplierItem
                      )}
                      value={optionsSelected.includes(
                        part.guidProcurementSupplierItem
                      )}
                      id={`id-registerSupplier-checkbox-${part.guidProcurementSupplierItem}`}
                    />
                  </S.Column>
                  <Collapse
                    in={openCollapseIndex === part.guidProcurementSupplierItem}>
                    <KOGFileUploader
                      isFullScreenDragDrop={true}
                      files={images}
                      setFiles={setImages}
                      onCustomInsert={newFiles => {
                        const id = part.guidProcurementSupplierItem;

                        const existingIndex = imagesGeneral.findIndex(
                          item => item.id === id
                        );

                        if (existingIndex !== -1) {
                          setImagesGeneral(prevData => {
                            const updatedData = [...prevData];
                            updatedData[existingIndex].files = [
                              ...updatedData[existingIndex].files,
                              ...newFiles
                            ];
                            return updatedData;
                          });
                        } else {
                          const newImageData = {
                            id,
                            files: newFiles
                          };
                          setImagesGeneral(prevData => [
                            ...prevData,
                            newImageData
                          ]);
                        }
                      }}
                      onCustomDelete={nameFile => {
                        setImagesGeneral(prevData => {
                          const updatedData = prevData.map(item => ({
                            id: item.id,
                            files: item.files.filter(
                              file => file.name !== nameFile
                            )
                          }));
                          return updatedData;
                        });

                        setImages(prevImages =>
                          prevImages.filter(file => file.name !== nameFile)
                        );
                      }}
                      typeFiles={'image'}
                    />
                  </Collapse>
                </S.ContainerPart>
              ))}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                <KogAutoCompleteControlled
                  id={`id-ViewOrder-select-reason-${index}`}
                  nameCustom={`reason-${supplier.guidSupplier}`}
                  isDefaultValueUnique
                  label={getTranslateByKey('information.reason')}
                  control={control}
                  rulesCustom={{
                    required:
                      formValues[supplier.guidSupplier]?.optionsSelected
                        ?.length > 0
                  }}
                  setValueCustom={setValue}
                  options={reasonReducer.data}
                  isLoading={reasonReducer.isLoading}
                  style={{ margin: '1rem 0' }}
                  onChangeCustom={option => {
                    if (option === null) {
                      setFormValues(prevValues => ({
                        ...prevValues,
                        [supplier.guidSupplier]: {
                          ...prevValues[supplier.guidSupplier],
                          reason: null
                        }
                      }));
                    } else {
                      setFormValues(prevValues => ({
                        ...prevValues,
                        [supplier.guidSupplier]: {
                          ...prevValues[supplier.guidSupplier],
                          reason: option
                        }
                      }));
                    }
                  }}
                />
                {formValues[supplier.guidSupplier]?.reason !== null &&
                  convertStringForBoolean(
                    formValues[supplier.guidSupplier]?.reason?.default
                  ) && (
                    <KogInputTextControlledNew
                      id={`id-reassignOrder-input-description-${index}`}
                      name={`description-${supplier.guidSupplier}`}
                      onChangeCustom={value => {
                        setFormValues(prevValues => ({
                          ...prevValues,
                          [supplier.guidSupplier]: {
                            ...prevValues[supplier.guidSupplier],
                            description: value
                          }
                        }));
                      }}
                      label={getTranslateByKey('information.description')}
                      style={{ marginBottom: '1rem' }}
                      control={control}
                      required
                    />
                  )}
              </div>
            </div>
          ))}
        </>
      ) : (
        <LoadingComponent />
      )}
      <KogModalComponent
        open={openModalConfirm}
        handleClose={() => setOpenModalConfirm(false)}
        title={getTranslateByKey('information.messageConfirmAction')}
        content={
          isModalCancel
            ? getTranslateByKey('information.areYouSureYouWantToCancelTheParts')
            : getTranslateByKey('information.areYouSureYouWantToReturnTheParts')
        }
        hasActionCancel={true}
        hasActionConfirm={true}
        labelActionCancel={getTranslateByKey('information.no')}
        labelActionConfirm={getTranslateByKey('information.yes')}
        onClickActionCancel={() => {}}
        onClickActionConfirm={handleConfirmAction}
        isLoadingActions={operationLoading}
      />
      <S.Footer>
        <SecondaryButton
          id="id-modalHistory-button-back"
          onClick={handleModalCancelReturn}>
          {getTranslateByKey('information.back')}
        </SecondaryButton>
        <SecondaryButton
          id="id-modalHistory-button-cancel"
          type="submit"
          isLoading={operationLoading}
          disabled={
            operationLoading || orderDataLoading || optionsSelected.length === 0
          }>
          {isModalCancel
            ? getTranslateByKey('information.cancel')
            : getTranslateByKey('information.return')}
        </SecondaryButton>
      </S.Footer>
    </S.Container>
  );
}

export default ModalCancelReturn;
